import Close from "@mui/icons-material/Close";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import type { IncidentWorkItemIdUnbranded } from "@thalamos/vision-common";

import api from "../../api/index.js";
import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";

export function CancelTransferModal({
  open,
  close,
  receivingOfficer,
  incidentWorkItemId,
  onTransferCancelled,
}: {
  open: boolean;
  close: () => void;
  receivingOfficer: string;
  incidentWorkItemId: IncidentWorkItemIdUnbranded;
  onTransferCancelled: () => Promisable<void>;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const notifications = useToastNotifications();
  return (
    <PopupDialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      disableDialogRootPadding={true}
    >
      <>
        <DialogTitle sx={{ fontSize: "1.250rem", fontWeight: "bold" }}>
          {t("admin.incidents.cancelTransferModal.title")}
          <IconButton
            aria-label="close"
            onClick={close}
            edge="end"
            sx={{
              position: "absolute",
              right: 15,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{ color: "primary", fontSize: "0.875rem" }}
            gutterBottom
          >
            {t("admin.incidents.cancelTransferModal.content", {
              receivingOfficer,
            })}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 3,
          }}
        >
          <Button onClick={close} color="error" variant="outlined">
            {t("admin.incidents.cancelTransferModal.close")}
          </Button>
          <Button
            onClick={() => {
              const handleRevokeTransfer = async () => {
                if (incidentWorkItemId) {
                  try {
                    const res = await api.incidentWorkItem.adminRevokeTransfer({
                      incidentWorkItemId,
                    });
                    if (res.status === 204 || res.status === 200) {
                      notifications.show(
                        t("incidentWorkItem.notificationCancelledShow"),
                        { severity: "success" },
                      );
                      await onTransferCancelled();
                    } else {
                      notifications.show(
                        t("incidentWorkItem.notificationCancelledError"),
                        { severity: "error" },
                      );
                    }
                  } catch (_error) {
                    notifications.show(
                      t("incidentWorkItem.notificationCancelledError"),
                      { severity: "error" },
                    );
                  }
                } else {
                  notifications.show(
                    t("incidentWorkItem.notificationCancelledError"),
                    { severity: "error" },
                  );
                }
              };

              void handleRevokeTransfer();
            }}
            color="primary"
            variant="contained"
          >
            {t("admin.incidents.cancelTransferModal.confirmCancel")}
          </Button>
        </DialogActions>
      </>
    </PopupDialog>
  );
}
